import { __awaiter } from '../../node_modules/tslib/tslib.es6.js';
import { addParamsToSearch } from '../../client/client.js';

/**
 * Performs a FHIR search and returns the results as a Bundle resource
 *
 * @param options FHIR resource type and FHIR search parameters
 * @param request optional OystehrClientRequest object
 * @returns FHIR Bundle resource
 */
function search(params, request) {
    return __awaiter(this, void 0, void 0, function* () {
        const { resourceType, params: searchParams } = params;
        let paramMap;
        if (searchParams) {
            paramMap = Object.entries(searchParams).reduce((acc, [_, param]) => {
                if (!acc[param.name]) {
                    acc[param.name] = [];
                }
                acc[param.name].push(param.value);
                return acc;
            }, {});
        }
        const requestBundle = yield this.fhirRequest(`/${resourceType}/_search`, 'POST')(paramMap, Object.assign(Object.assign({}, request), { contentType: 'application/x-www-form-urlencoded' }));
        const bundle = Object.assign(Object.assign({}, requestBundle), { entry: requestBundle.entry, unbundle: function () {
                var _a, _b;
                return (_b = (_a = this.entry) === null || _a === void 0 ? void 0 : _a.map((entry) => entry.resource).filter((entry) => entry !== undefined)) !== null && _b !== void 0 ? _b : [];
            } });
        return bundle;
    });
}
function create(params, request) {
    return __awaiter(this, void 0, void 0, function* () {
        const { resourceType } = params;
        return this.fhirRequest(`/${resourceType}`, 'POST')(params, request);
    });
}
function get({ resourceType, id }, request) {
    return __awaiter(this, void 0, void 0, function* () {
        return this.fhirRequest(`/${resourceType}/${id}`, 'GET')({}, request);
    });
}
function update(params, request) {
    return __awaiter(this, void 0, void 0, function* () {
        const { id, resourceType } = params;
        return this.fhirRequest(`/${resourceType}/${id}`, 'PUT')(params, Object.assign(Object.assign({}, request), { ifMatch: (request === null || request === void 0 ? void 0 : request.optimisticLockingVersionId) ? `W/"${request.optimisticLockingVersionId}"` : undefined }));
    });
}
function patch({ resourceType, id, operations }, request) {
    return __awaiter(this, void 0, void 0, function* () {
        return this.fhirRequest(`/${resourceType}/${id}`, 'PATCH')(operations, Object.assign(Object.assign({}, request), { contentType: 'application/json-patch+json', ifMatch: (request === null || request === void 0 ? void 0 : request.optimisticLockingVersionId) ? `W/"${request.optimisticLockingVersionId}"` : undefined }));
    });
}
function del({ resourceType, id }, request) {
    return __awaiter(this, void 0, void 0, function* () {
        return this.fhirRequest(`/${resourceType}/${id}`, 'DELETE')({}, request);
    });
}
function history({ resourceType, id, versionId }, request) {
    return __awaiter(this, void 0, void 0, function* () {
        return this.fhirRequest(`/${resourceType}/${id}/_history${versionId ? `/${versionId}` : ''}`, 'GET')({}, request);
    });
}
function batchInputRequestToBundleEntryItem(request) {
    const { method, url } = request;
    const baseRequest = {
        request: {
            method,
            url,
        },
    };
    // Escape query string parameters in entry.request.url
    if (url.split('?').length > 1) {
        const [resource, query] = url.split('?');
        const params = query
            .split('&')
            .map((param) => {
            const [name, value] = param.split('=');
            return { name, value };
        })
            .reduce((acc, { name, value }) => {
            if (!name) {
                return acc;
            }
            if (!acc[name]) {
                acc[name] = [];
            }
            acc[name].push(value);
            return acc;
        }, {});
        const search = new URLSearchParams();
        addParamsToSearch(params, search);
        baseRequest.request.url = `${resource}?${search.toString()}`;
    }
    // GET, DELETE, and HEAD require no further parameters
    if (['GET', 'DELETE', 'HEAD'].includes(method)) {
        return baseRequest;
    }
    // PUT updates require a full resource
    if (method === 'PUT') {
        const { resource } = request;
        return Object.assign(Object.assign({}, baseRequest), { resource: resource });
    }
    // PATCH can be Binary resource or JSON patch
    if (method === 'PATCH') {
        if ('resource' in request) {
            return Object.assign(Object.assign({}, baseRequest), { resource: request.resource });
        }
        return Object.assign(Object.assign({}, baseRequest), { resource: {
                resourceType: 'Binary',
                contentType: 'application/json-patch+json',
                data: Buffer.from(JSON.stringify(request.operations), 'utf8').toString('base64'),
            } });
    }
    // POST creates require a full resource
    if (method === 'POST') {
        const { resource, fullUrl } = request;
        return Object.assign(Object.assign({}, baseRequest), { resource: resource, fullUrl });
    }
    throw new Error('Unrecognized method');
}
function bundleRequest(type) {
    return function (input, request) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.fhirRequest('/', 'POST')({
                resourceType: 'Bundle',
                type,
                entry: input.requests.map(batchInputRequestToBundleEntryItem),
            }, request);
        });
    };
}
const batch = bundleRequest('batch');
const transaction = bundleRequest('transaction');
function formatAddress(address, options) {
    const builder = [];
    if (address.line) {
        builder.push(...address.line);
    }
    if (address.city || address.state || address.postalCode) {
        const cityStateZip = [];
        if (address.city) {
            cityStateZip.push(address.city);
        }
        if (address.state) {
            cityStateZip.push(address.state);
        }
        if (address.postalCode) {
            cityStateZip.push(address.postalCode);
        }
        builder.push(cityStateZip.join(', '));
    }
    if (address.use && ((options === null || options === void 0 ? void 0 : options.all) || (options === null || options === void 0 ? void 0 : options.use))) {
        builder.push('[' + address.use + ']');
    }
    return builder.join((options === null || options === void 0 ? void 0 : options.lineSeparator) || ', ').trim();
}
function formatHumanName(name, options) {
    const builder = [];
    if (name.prefix && (options === null || options === void 0 ? void 0 : options.prefix) !== false) {
        builder.push(...name.prefix);
    }
    if (name.given) {
        builder.push(...name.given);
    }
    if (name.family) {
        builder.push(name.family);
    }
    if (name.suffix && (options === null || options === void 0 ? void 0 : options.suffix) !== false) {
        builder.push(...name.suffix);
    }
    if (name.use && ((options === null || options === void 0 ? void 0 : options.all) || (options === null || options === void 0 ? void 0 : options.use))) {
        builder.push('[' + name.use + ']');
    }
    return builder.join(' ').trim();
}

export { batch, create, del as delete, formatAddress, formatHumanName, get, history, patch, search, transaction, update };
//# sourceMappingURL=fhir-ext.js.map
